document.addEventListener("DOMContentLoaded", () => {
  gsap.registerPlugin(ScrollTrigger, SplitText);

  // Safe JSON parse
  function parseJSON(str) {
    try { return str ? JSON.parse(str) : {}; }
    catch(e) { console.warn("Invalid JSON:", str); return {}; }
  }

  // Wait until element has rendered width (prevents SplitText measuring 0 width)
  function waitForRenderedText(el, timeout = 5000) {
    return new Promise(resolve => {
      const start = performance.now();
      (function check() {
        const width = el.getBoundingClientRect().width;
        const hasText = !!el.textContent.trim();
        if (width > 0 && hasText) return resolve(true);
        if (performance.now() - start > timeout) return resolve(false);
        requestAnimationFrame(check);
      })();
    });
  }

  // Wait for fonts ready as well (best-effort)
  async function ensureFontsReady() {
    if (document.fonts && document.fonts.ready) {
      try { await document.fonts.ready; } catch(e) { /* ignore */ }
    }
  }

  // Manual splitting fallback (returns NodeList / array of spans)
  function manualSplit(el, splitType = "chars") {
    const text = el.textContent || "";
    el.innerHTML = "";
    const frag = document.createDocumentFragment();
    const spans = [];

    if (splitType === "words") {
      const parts = text.match(/\s+|\S+/g) || [];
      parts.forEach(part => {
        const span = document.createElement("span");
        span.textContent = part;
        span.style.display = "inline-block";
        frag.appendChild(span);
        spans.push(span);
      });
    } else if (splitType === "lines") {
      const parts = text.split(/\r?\n/);
      parts.forEach((line, i) => {
        const span = document.createElement("span");
        span.textContent = line + (i < parts.length - 1 ? "\n" : "");
        span.style.display = "block";
        frag.appendChild(span);
        spans.push(span);
      });
    } else {
      for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        const span = document.createElement("span");
        span.textContent = ch;
        span.style.display = "inline-block";
        frag.appendChild(span);
        spans.push(span);
      }
    }

    el.appendChild(frag);
    return spans;
  }

  // Normalize trigger string: "selector|event|command"
  function parseTriggerDef(def) {
    const parts = def.split("|").map(s => s.trim()).filter(Boolean);
    return { selector: parts[0] || null, event: parts[1] || "click", command: parts[2] || "play" };
  }

  (async function init() {
    await ensureFontsReady();

    const animEls = document.querySelectorAll("[data-gsap]");
    const timelines = {};
    const tweens = new Map();

    for (const el of animEls) {
      const rawProps = parseJSON(el.dataset.gsapProps || "{}");
      const timelineName = el.dataset.gsapTimeline;
      const timelineAuto = el.dataset.gsapTimelineAuto === "true";
      const triggerAttr = el.dataset.gsapTrigger;
      const triggerDefs = triggerAttr ? triggerAttr.split(",").map(s => s.trim()).filter(Boolean) : [];

      // Force "from" type for split text animations with opacity 0
      let type = el.dataset.gsapType || "to";
      if (rawProps.splitText && rawProps.opacity === 0) {
        type = "from";
      }

      // ✅ Wait for text + fonts if splitText
      if (rawProps.splitText) {
        await waitForRenderedText(el);
        await ensureFontsReady();
      }

      const props = JSON.parse(JSON.stringify(rawProps || {}));
      let targets = el;
      let splitInstance = null;
      let splitTargets = null;

      // SplitText handling
      if (props.splitText) {
        const splitType = props.splitText.type || "chars";

        try {
          // Wait for rendered text + fonts
          await waitForRenderedText(el);
          await ensureFontsReady();

          // Create SplitText instance
          splitInstance = new SplitText(el, {
            type: splitType,
            ...props.splitText
          });

          // Get the appropriate split elements
          splitTargets = splitInstance[splitType];
          if (!splitTargets) {
            splitTargets = splitInstance.chars || splitInstance.words || splitInstance.lines;
          }

          if (splitTargets && splitTargets.length) {
            targets = splitTargets;
            
            // Make text visible initially for "from" animation
            gsap.set(el, { opacity: 1 });
            gsap.set(splitTargets, { opacity: 1 });
            
            // No need to modify the animation properties for "from" animation
            // They will be used as the starting point
          }
  } catch (err) {
    console.warn("SplitText failed, using fallback:", err);
    splitTargets = manualSplit(el, splitType);
    if (splitTargets.length) {
      targets = splitTargets;
      
      // Make text visible initially for "from" animation (consistent with main implementation)
      if (type === "from") {
        gsap.set(el, { opacity: 1 });
        gsap.set(splitTargets, { opacity: 1 });
      } else {
        const targetOpacity = props.opacity === 0 ? 1 : props.opacity || 1;
        const fromY = props.y || 0;
        
        gsap.set(splitTargets, { 
          opacity: 0,
          y: fromY
        });
        
        props.opacity = targetOpacity;
        props.y = 0;
      }
    }
  }

  // Clean up the splitText property
  delete props.splitText;
}
// ✅ END SPLITTEXT FIX


      else if (el.dataset.gsapTarget) {
        const sel = el.dataset.gsapTarget;
        try {
          const nodes = document.querySelectorAll(sel);
          targets = nodes.length ? nodes : el;
        } catch(e) {
          targets = el;
        }
      } else if (props.scrollTrigger) {
        // If ScrollTrigger is present and no specific target is set, 
        // automatically target immediate children
        const children = el.children;
        if (children.length > 1) {
          targets = children;
        }
      }

      const tweenConfig = Object.assign({}, props);
      
      // Handle stagger for multiple targets
      const hasMultipleTargets = targets && (Array.isArray(targets) || NodeList.prototype.isPrototypeOf(targets) || HTMLCollection.prototype.isPrototypeOf(targets)) && targets.length > 1;
      
      // Process stagger configuration
      let stagger = tweenConfig.stagger;
      delete tweenConfig.stagger;
      
      if (hasMultipleTargets && stagger !== undefined) {
        // Convert number to stagger object
        if (typeof stagger === 'number') {
          stagger = {
            amount: stagger,
            from: "start",
            ease: "none"
          };
        }
        // Add stagger to the configuration
        tweenConfig.stagger = stagger;
      }

      let tween;
      if (type === "fromTo") {
        if (!tweenConfig.from || !tweenConfig.to) {
          console.error("data-gsap-type='fromTo' requires { from, to } inside data-gsap-props");
          continue;
        }
        const fromObj = tweenConfig.from; delete tweenConfig.from;
        const toObj = tweenConfig.to; delete tweenConfig.to;
        
        // Add stagger to the toObj for fromTo animations
        if (hasMultipleTargets && stagger !== undefined) {
          toObj.stagger = stagger;
        }
        
        tween = gsap.fromTo(targets, fromObj, Object.assign({}, toObj, { paused: true }, tweenConfig));
      } else if (type === "from") {
        // For "from" animations, we need to ensure proper initialization
        gsap.set(targets, { opacity: 1 }); // Ensure targets are visible initially
        tween = gsap.from(targets, Object.assign({}, tweenConfig, { 
          paused: true,
          immediateRender: true // Important for "from" animations
        }));
      } else if (type === "to") {
        tween = gsap.to(targets, Object.assign({}, tweenConfig, { paused: true }));
      } else if (type === "set") {
        gsap.set(targets, tweenConfig);
        tween = gsap.to(targets, { duration: 0, paused: true });
      } else {
        console.warn("Unknown data-gsap-type:", type);
        continue;
      }

      tweens.set(el, tween);

      if (timelineName) {
        timelines[timelineName] = timelines[timelineName] || gsap.timeline({ paused: !timelineAuto });
        timelines[timelineName].add(tween, props.position || ">");
      } else if (!triggerDefs.length) {
        if (!rawProps.scrollTrigger) tween.play();
      }
    }

    // External trigger binding
    for (const el of animEls) {
      const triggerAttr = el.dataset.gsapTrigger;
      if (!triggerAttr) continue;
      const defs = triggerAttr.split(",").map(s => s.trim()).filter(Boolean);

      for (const def of defs) {
        const parsed = parseTriggerDef(def);
        const command = parsed.command || "play";
        const evt = parsed.event || "click";
        const triggerEls = document.querySelectorAll(parsed.selector);
        if (!triggerEls.length) continue;

        const timelineName = el.dataset.gsapTimeline;
        const targetTween = tweens.get(el);

        for (const trg of triggerEls) {
          trg.addEventListener(evt, () => {
            if (command.startsWith("seek:")) {
              const t = parseFloat(command.split(":")[1]) || 0;
              if (timelineName && timelines[timelineName]) timelines[timelineName].seek(t);
              else if (targetTween?.seek) targetTween.seek(t);
              return;
            }

            const cmd = command;
            const tl = timelineName ? timelines[timelineName] : null;
            const tw = targetTween;

            const run = obj => {
              if (!obj) return;
              if (cmd === "play") obj.play();
              else if (cmd === "pause") obj.pause();
              else if (cmd === "reverse") obj.reverse();
              else if (cmd === "restart") obj.restart();
              else if (cmd === "toggle") obj.isActive() ? obj.pause() : obj.play();
            };
            run(tl || tw);
          });
        }
      }
    }

    // Global controllers
    document.querySelectorAll("[data-gsap-control]").forEach(ctrl => {
      const defs = ctrl.dataset.gsapControl.split(",").map(s => s.trim()).filter(Boolean);
      defs.forEach(def => {
        const parts = def.split("|").map(s => s.trim());
        const selector = parts[0];
        const event = parts[1] || "click";
        const tlName = parts[2];
        const cmd = parts[3] || "play";
        const triggers = document.querySelectorAll(selector);
        triggers.forEach(t => {
          t.addEventListener(event, () => {
            const tl = timelines[tlName];
            if (!tl) return console.warn("Timeline not found:", tlName);
            if (cmd === "play") tl.play();
            else if (cmd === "pause") tl.pause();
            else if (cmd === "reverse") tl.reverse();
            else if (cmd === "restart") tl.restart();
            else if (cmd === "toggle") tl.isActive() ? tl.pause() : tl.play();
            else if (cmd.startsWith("seek:")) tl.seek(parseFloat(cmd.split(":")[1]) || 0);
          });
        });
      });
    });

    // Debug access
    window.__GSAP_TIMELINES__ = timelines;
    window.__GSAP_TWEENS__ = tweens;
  })();
});